// imports
import { Controller, useFormContext } from 'react-hook-form';
import { FC, Fragment, useCallback, useEffect, useState } from 'react';
import { Autocomplete, CircularProgress, MenuItem, TextField } from '@mui/material';
import { KeyboardArrowDownRounded as KeyboardArrowDownRoundedIcon } from '@mui/icons-material';
//interfaces, constants, helper, graphql
import { renderSweetener } from 'lib/helper';
import { HTTP_STATUS } from 'constants/index';
import { SweetenerSelectorProps, SelectType } from 'interfaces';
import { SweetenersPayload, useFindAllSweetenersLazyQuery } from 'generated/graphql';

const SweetenerSelector: FC<SweetenerSelectorProps> = ({
  name,
  type,
  nature,
  title,
  disabled,
  isRequired,
  defaultValue,
  handleChange,
  productTypeId,
  productSizeId,
  margin = 'dense',
  isMultiple = false,
  productFormulationTypeId,
}) => {
  const [options, setOptions] = useState<SweetenersPayload['data']>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const updatedOptions = [...renderSweetener(options ?? [])];
  const { control } = useFormContext();

  const [findAll, { loading }] = useFindAllSweetenersLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (res) => {
      const { findAllSweeteners } = res;
      const { data, response } = findAllSweeteners || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        setOptions(data as SweetenersPayload['data']);
      }
    },
    onError: () => {
      setOptions([]);
    },
  });

  const fetchSweeteners = useCallback(async () => {
    await findAll({
      variables: {
        findAllSweetenersInput: {
          paginationOptions: {
            page: 1,
            limit: 10,
          },
          ...(type && { type }),
          ...(nature && { nature }),
          ...(productSizeId && { productSizeId }),
          ...(productTypeId && { productTypeId }),
          ...(productFormulationTypeId && { productFormulationTypeId }),
        },
      },
    });
  }, [findAll, nature, productFormulationTypeId, productSizeId, productTypeId, type]);

  useEffect(() => {
    (!searchQuery.length || searchQuery.length > 1) && fetchSweeteners();
  }, [fetchSweeteners, searchQuery.length]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const { name, onBlur, onChange, value, ref } = field;
        const { error } = fieldState;
        const { message } = error || {};
        return (
          <Autocomplete
            ref={ref}
            multiple={isMultiple}
            onBlur={onBlur}
            disableClearable
            disabled={disabled}
            options={updatedOptions}
            value={isMultiple ? (value as SelectType[]) : (value as SelectType)}
            onChange={(_, value) => {
              handleChange && handleChange();
              onChange(value);
            }}
            popupIcon={<KeyboardArrowDownRoundedIcon />}
            getOptionLabel={(option) => option.name || ''}
            isOptionEqualToValue={({ value: option }, { value }) => value === option}
            renderInput={(params) => (
              <TextField
                {...params}
                name={name}
                margin={margin}
                variant="outlined"
                helperText={message}
                error={Boolean(error)}
                defaultValue={defaultValue}
                label={isRequired ? `${title} *` : title}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <Fragment>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </Fragment>
                  ),
                }}>
                {updatedOptions?.map((item) => {
                  const { name, value } = item;
                  return (
                    <MenuItem key={value} value={value}>
                      {name}
                    </MenuItem>
                  );
                })}
              </TextField>
            )}
          />
        );
      }}
    />
  );
};

export default SweetenerSelector;
